import React, {useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'
import {
    Box,
    Text,
    Stack,
    Link,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    Progress,
    Stepper,
    Step,
    StepTitle,
    StepDescription,
    StepIndicator,
    StepStatus,
    useSteps
} from '@chakra-ui/react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {LockIcon, CheckIcon} from '../icons'
import {useSiteContext} from '../../contexts/site'
import useTagManager from '../../hooks/use-tag-manager'

const Bmsm = ({
    size = 'pdp',
    dataLayerLoaded = true,
    hits = [],
    product = {},
    promoDetail,
    isPromoLoading
}) => {
    const {isYR} = useSiteContext()
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const tagManager = useTagManager()
    const styles = useMultiStyleConfig('Bmsm', {size})

    let hideMeter = false

    if (size === 'plp' && hits?.length && promoDetail?.id) {
        hideMeter = !hits?.some((h) =>
            h?.productPromotions?.some?.((p) => p.promotionId === promoDetail?.id)
        )
    }
    if (size === 'pdp' && product) {
        hideMeter = !product?.productPromotions?.some?.((p) => p.promotionId === promoDetail?.id)
    }

    const {config} = useMemo(() => {
        try {
            const config = promoDetail?.c_bmsmAndGwpMeterConfig
                ? JSON.parse(promoDetail.c_bmsmAndGwpMeterConfig)
                : ''
            return {config: config}
        } catch (e) {
            console.warn('Error parsing meter config:', e.message)
            return {config: ''}
        }
    }, [promoDetail])

    const {activeStep, setActiveStep} = useSteps({
        index: 0,
        count: config?.levels?.length
    })

    const max = config?.levels?.length ?? 1
    const progressPercent = activeStep === 0 ? 2 : (activeStep / max) * 100
    const promotionQtyInBasket = basket?.productItems
        ?.filter((item) =>
            item?.priceAdjustments?.some(
                (adjustment) => adjustment?.promotionId === promoDetail?.id
            )
        )
        ?.reduce((sum, item) => sum + (item?.quantity || 0), 0)

    function getLevel(promotionQtyInBasket, levels) {
        for (let i = 0; i < levels.length; i++) {
            const curr = levels[i]
            if (promotionQtyInBasket < curr?.threshold) {
                const prevThreshold = i > 0 ? levels[i - 1].threshold : 0
                return i + (promotionQtyInBasket - prevThreshold) / (curr.threshold - prevThreshold)
            }
        }
        return levels.length // max level if qty >= last threshold
    }

    const activePromotionLevel =
        promotionQtyInBasket && config?.levels?.length
            ? getLevel(promotionQtyInBasket, config?.levels)
            : 0

    useEffect(() => {
        if (activePromotionLevel !== undefined) {
            setActiveStep(activePromotionLevel)
        }
    }, [activePromotionLevel])

    useEffect(() => {
        if (
            !config?.meterType ||
            isPromoLoading ||
            !dataLayerLoaded ||
            isBasketLoading ||
            !promoDetail?.c_enableProgressMeter ||
            config?.meterType !== 'BMSM'
        ) {
            return
        }
        let progress
        if (!activePromotionLevel) {
            progress = 'Empty'
        } else if (activePromotionLevel > 0 && activePromotionLevel < config?.levels?.length) {
            progress = 'Mid'
        } else if (activePromotionLevel === config?.levels?.length) {
            progress = 'Full'
        }

        window.__dataLayerLoaded = true
        tagManager.promoMeterImpression(
            {
                id: promoDetail.id,
                name: promoDetail.name,
                creative: promoDetail.promoDetail,
                position: size
            },
            'BMSM',
            progress
        )
        window.__dataLayerLoaded = false
    }, [promoDetail, isPromoLoading, isBasketLoading, activePromotionLevel, dataLayerLoaded])

    if (config?.meterType === 'GWP' || hideMeter) {
        return
    }
    const isExternalDomain = config?.ctaLink?.indexOf('.com') !== -1
    const linkProps = isExternalDomain
        ? {href: config?.ctaLink}
        : {as: RouteLink, to: config?.ctaLink}
    const isCart = size === 'cart'
    const stepTitleProps = isCart
        ? {
              fontSize: '13px !important',
              fontStyle: 'normal',
              fontWeight: '300',
              color: 'black',
              lineHeight: '22px',
              letterSpacing: '0.065px'
          }
        : {}
    const stepDescriptionProps = isCart
        ? {
              fontSize: '13px !important',
              fontStyle: 'normal',
              fontWeight: '400',
              color: 'black',
              lineHeight: '22px',
              letterSpacing: '0.065px'
          }
        : {}
    const cartWrapperStyles = isCart
        ? {
              marginTop: {base: '30px', lg: '0px'}
          }
        : {}
    return config && config?.meterType === 'BMSM' ? (
        <Stack
            {...styles.wrapper}
            border={isCart ? '0px solid' : '1px solid'}
            className="bmsm-stack-wrapper"
            {...cartWrapperStyles}
        >
            <Stack {...styles.header} className="bmsm-stack-header">
                <Box className="bmsm-title-ends">
                    <Text
                        color="black"
                        className="bmsm-title"
                        fontFamily={isCart ? 'heading' : 'body'}
                        fontSize={isCart ? '20px' : '12px'}
                        fontWeight={isCart ? '400' : '600'}
                        lineHeight="25px"
                        letterSpacing="0.06px"
                    >
                        {config?.title}
                    </Text>

                    {config?.ends && (
                        <Text
                            color={isCart ? 'black' : 'gray.650'}
                            className="bmsm-ends"
                            fontSize={isCart ? '13px' : '10px'}
                            fontWeight="300"
                            data-date={config?.ends}
                            lineHeight="22px"
                            letterSpacing="0.05px"
                        >
                            {config?.ends}
                        </Text>
                    )}
                </Box>
                {isCart && config?.ctaMessage && config?.ctaLink && (
                    <Link
                        className="bmsm-link"
                        color="gray.650"
                        fontSize="13px"
                        fontWeight="300"
                        lineHeight="22px"
                        marginTop="4px"
                        textUnderlineOffset="4px"
                        textDecoration="underline"
                        letterSpacing="0.065px"
                        transition="color, 0.3s ease"
                        _hover={{
                            color: 'blue.700'
                        }}
                        {...linkProps}
                    >
                        {config?.ctaMessage}
                    </Link>
                )}
            </Stack>

            <Box {...styles.sliderWrapper} className="bmsm-slider-wrapper">
                <Stepper
                    size="sm"
                    index={activeStep}
                    gap="0"
                    variant={size}
                    className="bmsm-stepper"
                >
                    <Step opacity={0} className="bmsm-step-0">
                        <StepIndicator background="green.700" className="bmsm-step-indicator">
                            <StepStatus
                                className="bmsm-step-status"
                                active={
                                    <CheckIcon
                                        color="white"
                                        boxSize="14px"
                                        transform="translateX(1px) translateY(1px)"
                                    />
                                }
                                complete={<CheckIcon />}
                                incomplete={<LockIcon boxSize="12px" color="gray.500" />}
                            />
                        </StepIndicator>

                        <Box
                            position="absolute"
                            className="bmsm-step-title-description"
                            transform="translateY(38px) translateX(-25%)"
                            width="max-content"
                            textAlign="center"
                            sx={{
                                '.chakra-step__title, .chakra-step__description': {
                                    fontSize: '11px'
                                }
                            }}
                            marginTop={isCart ? '17px' : '0px'}
                        >
                            <StepTitle className="bmsm-step-title" {...stepTitleProps} />
                            <StepDescription
                                className="bmsm-step-description"
                                {...stepDescriptionProps}
                            />
                        </Box>
                    </Step>

                    {config?.levels?.map((level, index) => (
                        <Step key={index} className={`bmsm-step-` + index}>
                            <StepIndicator
                                className="bmsm-step-indicator"
                                data-active-step={activeStep}
                                data-index={index + 1}
                                background={
                                    activeStep >= index + 1
                                        ? isYR
                                            ? 'black.300 !important'
                                            : 'green.700 !important'
                                        : 'white'
                                }
                            >
                                <StepStatus
                                    className="bmsm-step-status"
                                    active={
                                        <CheckIcon
                                            color="white"
                                            boxSize="14px"
                                            transform="translateX(2px) translateY(1.8px)"
                                        />
                                    }
                                    complete={
                                        <CheckIcon
                                            color="white"
                                            boxSize="14px"
                                            transform="translateX(2px) translateY(1.8px)"
                                        />
                                    }
                                    incomplete={<LockIcon boxSize="12px" color="gray.500" />}
                                />
                            </StepIndicator>

                            <Box
                                position="absolute"
                                transform="translateY(24px) translateX(-50%)"
                                width="max-content"
                                textAlign="right"
                                sx={{
                                    '.chakra-step__title, .chakra-step__description': {
                                        fontSize: '11px'
                                    }
                                }}
                                className="bmsm-step-title-description"
                                marginTop={isCart ? '17px' : '0px'}
                            >
                                <StepTitle className="bmsm-step-title" {...stepTitleProps}>
                                    {level.message1}
                                </StepTitle>
                                <StepDescription
                                    className="bmsm-step-description"
                                    {...stepDescriptionProps}
                                >
                                    {level.message2}
                                </StepDescription>
                            </Box>
                        </Step>
                    ))}
                </Stepper>
                <Box
                    sx={{
                        'div[role="progressbar"]': {
                            backgroundColor: isYR ? 'gray.50' : 'green.150'
                        }
                    }}
                    className="bmsm-progress-wrapper"
                >
                    <Progress
                        value={progressPercent}
                        position="absolute"
                        width="full"
                        top="5.5px"
                        zIndex={-1}
                        className="bmsm-progress"
                    />
                </Box>
            </Box>
        </Stack>
    ) : null
}

Bmsm.propTypes = {
    hits: PropTypes.array,
    product: PropTypes.object,
    dataLayerLoaded: PropTypes.bool,
    size: PropTypes.string,
    promoDetail: PropTypes.shape({
        id: PropTypes.string,
        c_bmsmAndGwpMeterConfig: PropTypes.string,
        c_enableProgressMeter: PropTypes.bool,
        name: PropTypes.string,
        promoDetail: PropTypes.object
    }),
    isPromoLoading: PropTypes.bool
}

export default Bmsm
